import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Flag, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import reportService from '../../services/reportService';
import toast from 'react-hot-toast';

const REPORT_REASONS = [
  'Scam or Fraudulent Activity',
  'Misleading Information',
  'Violates Terms of Service',
  'Intellectual Property Violation',
  'Inappropriate Content',
  'Spam',
  'Other'
];

const ReportProjectModal = ({ project, isOpen, onClose }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setDetails('');
      setIsSubmitting(false);
      setIsSuccess(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    if (!details || details.length < 10) {
      toast.error('Please provide more details (at least 10 characters)');
      return;
    }

    setIsSubmitting(true);

    try {
      await reportService.submitReport(project.id, {
        reason,
        details
      });
      
      setIsSuccess(true);
      toast.success('Project reported successfully. Our team will review it.');
      
      // Auto close after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Report submission error:', error);
      toast.error(error.message || 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="border-0 shadow-2xl bg-white">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-xl text-neutral-900">
                  <div className="w-8 h-8 bg-secondary-red/10 rounded-lg flex items-center justify-center">
                    <Flag className="w-5 h-5 text-secondary-red" />
                  </div>
                  Report Campaign
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="h-8 w-8 rounded-full"
                  disabled={isSubmitting}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              {isSuccess ? (
                <div className="py-8 text-center space-y-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900">Report Submitted</h3>
                  <p className="text-neutral-600">
                    Thank you for helping keep Crowblock safe. Our administrators will review this report shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
                    <Flag className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800 leading-relaxed">
                      If you believe this project is a scam, misleading, or violates our terms, please let us know. 
                      <strong> Your identity will remain anonymous to the project owner.</strong>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-neutral-900">
                      Reason for Reporting
                    </label>
                    <select
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required
                      className="w-full px-4 py-3 border-2 border-neutral-100 rounded-xl focus:border-primary-teal focus:outline-none transition-colors bg-neutral-50"
                    >
                      <option value="">Select a reason...</option>
                      {REPORT_REASONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-neutral-900">
                      Additional Details
                    </label>
                    <textarea
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      required
                      rows={4}
                      placeholder="Please provide specific details about why you are reporting this project..."
                      className="w-full px-4 py-3 border-2 border-neutral-100 rounded-xl focus:border-primary-teal focus:outline-none transition-colors bg-neutral-50 resize-none"
                    />
                    <p className="text-xs text-neutral-500">
                      Provide as much information as possible to help our team verify your report.
                    </p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-secondary-red hover:bg-secondary-red/90 text-white"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Report'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReportProjectModal;
